// A boundary for ONE screen, not the whole game (TAS-26).
//
// The app used to have a single root SentryErrorBoundary: any render crash on
// any screen — one bad team payload on the scoreboard, one null field on a
// reveal card — replaced the entire game with "Something went wrong · Refresh
// page" for that player, and tore down the realtime provider with it. Refresh
// meant a full re-join mid-round.
//
// This boundary sits INSIDE GameFlowProvider, around the routed screen only, so
// the provider (channels, live-state reconcile, step tracking) survives the
// crash. It RESETS — not remounts — when the step or route changes: a healthy
// screen is never remounted (Game.jsx keeps its per-question state across
// same-path steps exactly as before), and a player stuck on the fallback is
// released the moment the GM advances past the broken screen. Until then they
// get a small card with "Try again" (re-render this screen) and "Rejoin" (a
// soft reload of the current route).
import { Component, useContext } from 'react';
import * as Sentry from '@sentry/react';
import WhatsAppSupportLink from './shared/WhatsAppSupportLink';
import { useLocation, useNavigate } from 'react-router-dom';
import { GameFlowContext } from '../contexts/GameFlowProvider';
import { isChunkLoadError } from '../utils/lazyRetry';

function ScreenFallback({ error, resetError, stepIndex }) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <div
      role="alert"
      className="flex min-h-[100dvh] items-center justify-center bg-wine-background px-6"
    >
      <div className="w-full max-w-sm rounded-2xl border border-wine-border bg-wine-card p-6 text-center shadow-lg">
        <h2 className="font-display text-xl font-semibold text-wine-text-heading">
          This screen hit a snag
        </h2>
        <p className="mt-2 text-sm text-wine-text-body">
          You&rsquo;re still in the game — the room carries on and this will clear
          on the next step. Or:
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            // A stale chunk (a deploy landed mid-game) cannot be fixed by
            // re-rendering the same import; only a fresh index.html knows the
            // new hashes. Everything else re-renders in place as before.
            onClick={() => (isChunkLoadError(error) ? window.location.reload() : resetError())}
            className="w-full rounded-xl bg-wine-primary py-3 text-sm font-semibold text-white"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => {
              resetError();
              // Same route, fresh mount — the provider re-syncs from live state.
              navigate(location.pathname + location.search, { replace: true, state: { screenRecover: Date.now() } });
            }}
            className="w-full rounded-xl border border-wine-border bg-white py-3 text-sm font-semibold text-wine-text-heading"
          >
            Rejoin this screen
          </button>
        </div>
        <p className="mt-4 text-center text-xs text-wine-text-body">
          Still stuck?{' '}
          <WhatsAppSupportLink error={error} step={Number.isInteger(stepIndex) ? stepIndex : undefined} className="font-semibold text-wine-primary underline" />
        </p>
        {error && import.meta.env.MODE === 'development' && (
          <pre className="mt-4 max-h-40 overflow-auto rounded bg-stone-100 p-2 text-left text-[11px] text-stone-700">
            step {String(stepIndex)} · {String(error?.message || error)}
          </pre>
        )}
      </div>
    </div>
  );
}

// The boundary proper. A class because only class components can catch render
// errors; `resetKey` (route|step) clears a caught error on change WITHOUT
// remounting children when there is none — the difference between "the GM
// advancing heals the stuck player" and "every step remounts every screen".
class ScreenBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.resetError = () => this.setState({ error: null });
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    Sentry.captureException(error, {
      tags: { errorBoundary: 'screen', screen_step: String(this.props.stepIndex), route: this.props.route },
      contexts: { react: { componentStack: info?.componentStack } },
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      // The room moved on (or the route changed) — let the player back in.
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return <ScreenFallback error={this.state.error} resetError={this.resetError} stepIndex={this.props.stepIndex} />;
    }
    return this.props.children;
  }
}

export default function ScreenErrorBoundary({ children }) {
  // Context may be absent (routes outside the game flow); the boundary still works.
  const flow = useContext(GameFlowContext);
  const stepIndex = flow?.stepIndex ?? null;
  const location = useLocation();
  return (
    <ScreenBoundaryInner
      resetKey={`${location.pathname}|${stepIndex}`}
      stepIndex={stepIndex}
      route={location.pathname}
    >
      {children}
    </ScreenBoundaryInner>
  );
}
